import { useEffect, useState } from 'react'

import { Input } from '@/frontend/components/ui/input'

interface NumericInputProps
  extends Omit<React.ComponentProps<'input'>, 'value' | 'onChange' | 'type'> {
  value: number | null
  emptyValue?: number | null
  /** Decimal places between the value and what the input shows, e.g. 2 to display basis points as a percentage. */
  scale?: number
  onChange: (value: number | null) => void
}

/**
 * A controlled numeric input that handles string↔number conversion.
 * Accepts `number | null`, manages internal string state for editing,
 * and commits parsed values on blur.
 */
export function NumericInput({
  value,
  emptyValue = null,
  scale = 0,
  onChange,
  onBlur,
  ...props
}: NumericInputProps) {
  const factor = 10 ** scale
  const [draft, setDraft] = useState(() => formatValue(value, factor))

  // Sync draft when external value changes (e.g. form reset)
  useEffect(() => {
    setDraft(formatValue(value, factor))
  }, [value, factor])

  const handleBlur = (e: React.FocusEvent<HTMLInputElement>) => {
    const parsed = draft.trim() === '' ? emptyValue : parseValue(draft, factor)
    onChange(parsed)
    // Re-format to clean up input (e.g. "  5  " → "5")
    setDraft(formatValue(parsed, factor))
    onBlur?.(e)
  }

  return (
    <Input
      type="text"
      inputMode="decimal"
      value={draft}
      onChange={(e) => setDraft(e.target.value)}
      onBlur={handleBlur}
      {...props}
    />
  )
}

function formatValue(value: number | null, factor: number): string {
  return value != null ? String(value / factor) : ''
}

function parseValue(str: string, factor: number): number | null {
  const trimmed = str.trim()
  if (trimmed === '') return null
  const num = Number(trimmed)
  if (Number.isNaN(num)) return null
  return factor === 1 ? num : Math.round(num * factor)
}
